// Iter-f6 — Brand Staff Utilisation Dashboard (summary / branch cards).
//
// Lives under Brand workspace → Overview sub-menu, alongside Brand
// Overview, Cancellation, and Service Performance. Mirrors their
// visual language and A-vs-B date-range comparator. Branch cards
// show the average utilisation across eligible staff (Doctor /
// Nurse / Therapist) for Period A plus the % delta vs Period B.
//
// Tapping a card opens /staff/brand/staff-utilisation/:bid.
// No engine files are imported or edited.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RefreshCw, ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";
import StaffShell from "@/components/layout/StaffShell";
import { staffApi, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { isOwner } from "@/lib/AuthEngine";
import { todayBali } from "@/lib/ReportingEngine";
import { InfoHint, InfoHintInline } from "@/components/common/InfoHint";
import { STAFF_UTILISATION_TIPS } from "@/lib/reportTips";

// Iter-f7 — Default-range rule for every Overview dashboard:
//   Period A = first → last day of the PREVIOUS calendar month
//   Period B = first day of CURRENT month → today (Bali)
// Both ranges remain editable after mount; this is only the on-load default.
function previousMonthRange(todayISO) {
  const [y, m] = todayISO.split("-").map(Number);
  const prevYear  = m === 1 ? y - 1 : y;
  const prevMonth = m === 1 ? 12 : m - 1;
  const lastDay   = new Date(Date.UTC(prevYear, prevMonth, 0)).getUTCDate();
  const mm = String(prevMonth).padStart(2, "0");
  const dd = String(lastDay).padStart(2, "0");
  return [`${prevYear}-${mm}-01`, `${prevYear}-${mm}-${dd}`];
}
function currentMonthToToday(todayISO) {
  const [y, m] = todayISO.split("-");
  return [`${y}-${m}-01`, todayISO];
}

export default function BrandStaffUtilisationDashboard() {
  const { staff } = useAuth();
  const nav = useNavigate();
  const today = todayBali();
  const [aFrom, setAFrom] = useState(() => previousMonthRange(today)[0]);
  const [aTo,   setATo]   = useState(() => previousMonthRange(today)[1]);
  const [bFrom, setBFrom] = useState(() => currentMonthToToday(today)[0]);
  const [bTo,   setBTo]   = useState(() => currentMonthToToday(today)[1]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  const [tick, setTick] = useState(false);
  useEffect(() => {
    let cancelled = false;
    staffApi.get("/report-permissions/me", { params: { scope: "brand" } })
      .then((r) => {
        if (cancelled) return;
        const keys = new Set(r.data?.allowed_keys || []);
        setTick(keys.has("staff_utilisation"));
      })
      .catch(() => { /* backend remains authority */ });
    return () => { cancelled = true; };
  }, []);
  const canSee = isOwner(staff) || tick;

  const params = useMemo(() => ({
    a_from: aFrom, a_to: aTo, b_from: bFrom, b_to: bTo,
  }), [aFrom, aTo, bFrom, bTo]);

  const load = useCallback(() => {
    if (!canSee) return;
    setLoading(true);
    staffApi.get("/reports/brand/staff-utilisation/summary", { params })
      .then((r) => setSummary(r.data))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, [canSee, params]);
  useEffect(() => { load(); }, [load]);

  const branches = summary?.branches || [];

  return (
    <StaffShell context="brand">
      <div className="flex items-end justify-between mb-4">
        <div>
          <h1 className="text-2xl font-semibold">Staff Utilisation</h1>
          <p className="text-xs font-medium text-sky-600 mt-1" data-testid="brand-util-label">
            Period A · {aFrom} → {aTo} &nbsp;vs&nbsp; Period B · {bFrom} → {bTo}
          </p>
        </div>
      </div>

      {!canSee && (
        <Card className="rounded-lg mb-6">
          <CardContent className="p-8 text-sm text-muted-foreground text-center">
            You don't have permission to view the Staff Utilisation dashboard.
          </CardContent>
        </Card>
      )}

      {canSee && (
        <>
          <Card className="rounded-lg mb-4" data-testid="brand-util-filters">
            <CardContent className="p-3 flex items-center gap-3 flex-wrap">
              <RangePicker label="Period A" from={aFrom} to={aTo} onFrom={setAFrom} onTo={setATo} testIdPrefix="brand-util-a" />
              <span className="h-6 w-px bg-border mx-1" />
              <RangePicker label="Period B" from={bFrom} to={bTo} onFrom={setBFrom} onTo={setBTo} testIdPrefix="brand-util-b" />
              <Button
                size="sm"
                variant="outline"
                onClick={load}
                disabled={loading}
                className="gap-1 ml-auto"
                data-testid="brand-util-refresh"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
              </Button>
            </CardContent>
          </Card>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 mb-6">
            <TotalCard
              label="BRAND AVG · A"
              value={summary?.grand_utilisation_pct_a}
              baseline={summary?.grand_utilisation_pct_b}
              delta={summary?.grand_delta_pct}
              tone="sky"
              loading={loading}
              testId="brand-util-total-grand"
              caption="Weighted avg across eligible staff"
              tooltip={STAFF_UTILISATION_TIPS.utilisation}
            />
            <TotalCard
              label="BRAND AVG · B"
              value={summary?.grand_utilisation_pct_b}
              tone="muted"
              loading={loading}
              testId="brand-util-total-grand-b"
              caption={`${bFrom} → ${bTo}`}
              tooltip={STAFF_UTILISATION_TIPS.utilisation}
            />
            <TotalCard
              label="BRANCHES REPORTED"
              value={branches.length}
              tone="amber"
              suffix=""
              loading={loading}
              testId="brand-util-total-branches"
              caption="Branches with eligible staff"
              integer
              tooltip={STAFF_UTILISATION_TIPS.utilisation}
            />
          </div>

          <p className="tracking-overline text-[10px] text-muted-foreground mb-3">BY LOCATION</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {branches.map((b) => {
              const slug = (b.branch_name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-");
              return (
                <Card
                  key={b.branch_id}
                  onClick={() => nav(`/staff/brand/staff-utilisation/${b.branch_id}?a_from=${aFrom}&a_to=${aTo}&b_from=${bFrom}&b_to=${bTo}`)}
                  className="rounded-lg cursor-pointer transition-shadow hover:shadow-md hover:ring-1 hover:ring-primary/30 relative"
                  data-testid={`brand-util-branch-${slug}`}
                >
                  <InfoHint text={STAFF_UTILISATION_TIPS.utilisation} testId={`brand-util-branch-${slug}-info`} />
                  <CardContent className="p-5">
                    <p className="font-medium pr-5">{b.branch_name}</p>
                    <div className="mt-4">
                      <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                        Avg Utilisation · A
                        <InfoHintInline text={STAFF_UTILISATION_TIPS.utilisation} testId={`brand-util-branch-${slug}-util-info`} />
                      </p>
                      <p className="text-3xl font-semibold mt-0.5 text-sky-600 tabular-nums" data-testid={`brand-util-branch-${slug}-pct`}>
                        {(b.utilisation_pct_a ?? 0).toFixed(1)}%
                      </p>
                      <DeltaBadge delta={b.delta_pct} testId={`brand-util-branch-${slug}-delta`} />
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        B · {(b.utilisation_pct_b ?? 0).toFixed(1)}%
                      </p>
                    </div>
                    <div className="mt-3 text-[11px] text-muted-foreground flex items-center justify-between">
                      <span>Eligible: {b.eligible_staff_count_a ?? 0}</span>
                      <span className="text-primary/80 uppercase tracking-wider text-[10px]">Open →</span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
            {branches.length === 0 && !loading && (
              <Card className="rounded-lg sm:col-span-2 lg:col-span-3">
                <CardContent className="p-8 text-sm text-muted-foreground text-center">
                  No eligible staff schedule in the selected Period A.
                </CardContent>
              </Card>
            )}
          </div>
        </>
      )}
    </StaffShell>
  );
}

function RangePicker({ label, from, to, onFrom, onTo, testIdPrefix }) {
  return (
    <div className="flex items-center gap-2">
      <span className="tracking-overline text-[10px] text-muted-foreground">{label}</span>
      <Input type="date" value={from} onChange={(e) => onFrom(e.target.value)} className="h-8 w-40 text-xs" data-testid={`${testIdPrefix}-from`} />
      <span className="text-xs text-muted-foreground">to</span>
      <Input type="date" value={to}   onChange={(e) => onTo(e.target.value)}   className="h-8 w-40 text-xs" data-testid={`${testIdPrefix}-to`} />
    </div>
  );
}

function TotalCard({ label, value, baseline, delta, tone, loading, testId, caption, integer = false, tooltip }) {
  const toneColor = { sky: "text-sky-600", amber: "text-amber-600", muted: "text-muted-foreground" }[tone] || "text-foreground";
  const display = loading
    ? "…"
    : integer
      ? Number(value ?? 0).toLocaleString("en-US")
      : `${Number(value ?? 0).toFixed(1)}%`;
  return (
    <Card className="rounded-lg relative" data-testid={testId}>
      <InfoHint text={tooltip} testId={`${testId}-info`} />
      <CardContent className="p-5">
        <p className="tracking-overline text-[10px] text-muted-foreground pr-5">{label}</p>
        <p className={`text-3xl font-semibold mt-2 ${toneColor}`}>{display}</p>
        {delta !== undefined && (
          <div className="mt-1.5 flex items-center justify-between gap-2 text-[11px]">
            <DeltaBadge delta={delta} testId={`${testId}-delta`} />
            {baseline !== undefined && (
              <span className="text-muted-foreground" data-testid={`${testId}-b`}>
                B · {Number(baseline ?? 0).toFixed(1)}%
              </span>
            )}
          </div>
        )}
        {caption && <p className="text-[11px] text-muted-foreground mt-2">{caption}</p>}
      </CardContent>
    </Card>
  );
}

function DeltaBadge({ delta, testId }) {
  let Icon = Minus;
  let cls = "text-muted-foreground";
  if (delta != null && delta > 0) { Icon = ArrowUpRight; cls = "text-emerald-600"; }
  if (delta != null && delta < 0) { Icon = ArrowDownRight; cls = "text-rose-600"; }
  const label = delta == null ? "—" : `${delta > 0 ? "+" : ""}${delta}%`;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] font-medium ${cls}`} data-testid={testId}>
      <Icon className="w-3 h-3" /> {label}
    </span>
  );
}
